import {
  BOARD_VISIBILITIES,
  SUPERADMIN_AUDIT_ACTIONS,
  type AdminStream,
} from '@molt/shared';
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import {
  ADMIN_SELECT_CLASS,
  AdminConsole,
  AdminPagedPanel,
  AdminSearchField,
} from '@/components/admin/AdminConsole';
import { AdminAuditTable, adminAuditActionKey } from '@/components/admin/AdminAuditTable';
import { AdminBoardTable, adminVisibilityKey } from '@/components/admin/AdminBoardTable';
import { AdminCreateUser } from '@/components/admin/AdminCreateUser';
import { AdminUserTable } from '@/components/admin/AdminUserTable';
import { AdminWorkspaceTable } from '@/components/admin/AdminWorkspaceTable';
import { FilterChip } from '@/components/FilterChip';
import { Skeleton } from '@/components/Skeleton';
import { RouteNotFound } from '@/components/shell/RouteNotFound';
import {
  adminConsoleSearchFrom,
  adminConsoleTab,
  useAdminAudit,
  useAdminAuditLoadMore,
  useAdminBoards,
  useAdminBoardsLoadMore,
  useAdminUsers,
  useAdminUsersLoadMore,
  useAdminWorkspaces,
  useAdminWorkspacesLoadMore,
  type AdminConsoleSearch,
  type AdminLoadMore,
} from '@/lib/admin';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { isSuperadminSession, sessionQueryOptions, useSession } from '@/lib/session';

/**
 * `/admin` — SA-9's platform admin console, and the parent of
 * `/admin/users/$userId`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE ORDER OF THE THREE EARLY RETURNS IS THE SECURITY-RELEVANT PART
 * ---------------------------------------------------------------------------
 *   1. pending → a skeleton. We do not know yet, and D12's rule is that neither
 *      branch may be drawn.
 *
 *      ⚠ Pending BEFORE THE FIRST ANSWER only (`isFetched`), and a render loop
 *      is why. `GET /me` answers a signed-out visitor with a 401 and no data,
 *      and a query with no data that refetches goes back to `pending` with its
 *      error cleared. `RouteNotFound` calls `useSession` itself, so mounting it
 *      refetches the stale errored query — which, with a plain `isPending`
 *      check, swapped the page back to the skeleton, unmounted `RouteNotFound`,
 *      got the 401 again and mounted it again, forever. Once the session has
 *      answered, a refetch keeps the Not found page up and the loop never
 *      starts. `routes/admin.test.tsx`'s signed-out case is the one that sees
 *      it.
 *   2. signed out, no profile, or not a superadmin → `RouteNotFound`, exactly
 *      the page an unmatched URL renders. The brief's "ordinary Not found": the
 *      console must not be an existence oracle any more than the API is.
 *   3. a child path → `<Outlet/>`, the `power-ups.tsx` pattern. Without it the
 *      detail page never renders, because this component matches it too.
 *
 * (2) comes BEFORE (3), so the child route inherits the gate: a normal account
 * on `/admin/users/<id>` gets Not found from here and the child never mounts.
 * The web enforces nothing — every `/admin` route answers a non-superadmin with
 * the unmatched-route 404 — but the brief asks that no admin request is made at
 * all, and every read in `lib/admin.ts` is gated on the same predicate.
 *
 * Tabs and filters live in the query string (`validateSearch`), replaced rather
 * than pushed, so Back leaves the console instead of stepping through filters.
 */

export const Route = createFileRoute('/admin')({
  validateSearch: (search: Record<string, unknown>): AdminConsoleSearch =>
    adminConsoleSearchFrom(search),
  component: AdminRoute,
});

function AdminRoute(): ReactElement {
  const location = useLocation();
  const session = useSession();
  const { isFetched } = useQuery(sessionQueryOptions());

  if (session.isPending && !isFetched) {
    return (
      <div aria-busy="true" className="mx-auto flex w-full max-w-6xl flex-col gap-4 py-2">
        <Skeleton shape="line" width="12rem" />
        <Skeleton shape="chip" count={4} />
        <Skeleton shape="row" count={6} />
      </div>
    );
  }

  if (!isSuperadminSession(session)) {
    return <RouteNotFound />;
  }

  if (location.pathname !== '/admin') {
    return <Outlet />;
  }

  return <AdminConsoleIndex />;
}

/**
 * The index: owns the address, the four reads and their "Load more".
 *
 * ⚠ The search is read from `useLocation()`, the same source as the pathname
 * check that mounted this component, and NOT from `Route.useSearch()`. `/admin`
 * is also matched as the PARENT of `/admin/users/$userId`, with that page's
 * empty search; on the navigation from an account page to
 * `/admin?tab=audit&target=…`, the location updates a render before the match
 * does, so `Route.useSearch()` still answered `{}` — the Users tab — and the
 * console fetched the whole account list on the way to the audit log.
 */
function AdminConsoleIndex(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const search = adminConsoleSearchFrom(location.search);
  const tab = adminConsoleTab(search);
  const { t } = useInterfaceLanguage();
  const [draftQuery, setDraftQuery] = useState(search.q ?? '');

  useEffect(() => {
    setDraftQuery(search.q ?? '');
  }, [search.q, tab]);

  const userFilters = { q: search.q };
  const workspaceFilters = { q: search.q };
  const boardFilters = {
    q: search.q,
    visibility: search.visibility,
    closed: search.closed,
  };
  const auditFilters = {
    actor: search.actor,
    target: search.target,
    action: search.action,
  };

  // Only the showing tab reads; the other three stay idle until selected.
  const users = useAdminUsers(userFilters, tab === 'users');
  const workspaces = useAdminWorkspaces(workspaceFilters, tab === 'workspaces');
  const boards = useAdminBoards(boardFilters, tab === 'boards');
  const audit = useAdminAudit(auditFilters, tab === 'audit');
  const moreUsers = useAdminUsersLoadMore(userFilters);
  const moreWorkspaces = useAdminWorkspacesLoadMore(workspaceFilters);
  const moreBoards = useAdminBoardsLoadMore(boardFilters);
  const moreAudit = useAdminAuditLoadMore(auditFilters);

  /** Undefined values drop out: `adminConsoleSearchFrom` keeps only what it can validate. */
  function replaceSearch(next: Readonly<Record<string, unknown>>): void {
    void navigate({
      to: '/admin',
      search: adminConsoleSearchFrom({ ...next }),
      replace: true,
    });
  }

  function changeTab(next: AdminStream): void {
    // Filters do not carry across tabs: each tab's `q` means something else.
    replaceSearch(next === 'users' ? {} : { tab: next });
  }

  function submitQuery(q: string): void {
    replaceSearch({ ...search, q: q === '' ? undefined : q });
  }

  const filters =
    tab === 'users' ? (
      <>
        <AdminSearchField
          label={t('admin.search.usersLabel')}
          hint={t('admin.search.usersHint')}
          value={draftQuery}
          onChange={setDraftQuery}
          onSubmit={submitQuery}
        />
        <AdminCreateUser />
      </>
    ) : tab === 'workspaces' ? (
      <AdminSearchField
        label={t('admin.search.workspacesLabel')}
        hint={t('admin.search.workspacesHint')}
        value={draftQuery}
        onChange={setDraftQuery}
        onSubmit={submitQuery}
      />
    ) : tab === 'boards' ? (
      <>
        <AdminSearchField
          label={t('admin.search.boardsLabel')}
          hint={t('admin.search.boardsHint')}
          value={draftQuery}
          onChange={setDraftQuery}
          onSubmit={submitQuery}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('admin.filter.visibilityLabel')}
          <select
            className={ADMIN_SELECT_CLASS}
            value={search.visibility ?? ''}
            onChange={(event) =>
              replaceSearch({
                ...search,
                visibility:
                  event.currentTarget.value === ''
                    ? undefined
                    : event.currentTarget.value,
              })
            }
          >
            <option value="">{t('admin.filter.visibilityAny')}</option>
            {BOARD_VISIBILITIES.map((visibility) => (
              <option key={visibility} value={visibility}>
                {t(adminVisibilityKey(visibility))}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('admin.filter.closedLabel')}
          <select
            className={ADMIN_SELECT_CLASS}
            value={search.closed === undefined ? '' : String(search.closed)}
            onChange={(event) => {
              const value = event.currentTarget.value;
              replaceSearch({
                ...search,
                closed: value === '' ? undefined : value === 'true',
              });
            }}
          >
            <option value="">{t('admin.filter.closedAny')}</option>
            <option value="false">{t('admin.filter.closedFalse')}</option>
            <option value="true">{t('admin.filter.closedTrue')}</option>
          </select>
        </label>
      </>
    ) : (
      <>
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('admin.filter.actionLabel')}
          <select
            className={ADMIN_SELECT_CLASS}
            value={search.action ?? ''}
            onChange={(event) =>
              replaceSearch({
                ...search,
                action:
                  event.currentTarget.value === ''
                    ? undefined
                    : event.currentTarget.value,
              })
            }
          >
            <option value="">{t('admin.filter.actionAny')}</option>
            {SUPERADMIN_AUDIT_ACTIONS.map((action) => (
              <option key={action} value={action}>
                {t(adminAuditActionKey(action))}
              </option>
            ))}
          </select>
        </label>
        {search.actor === undefined ? null : (
          <FilterChip
            className="sm:mt-6"
            facet={t('admin.filter.actor')}
            value={search.actor}
            onRemove={() => replaceSearch({ ...search, actor: undefined })}
          />
        )}
        {search.target === undefined ? null : (
          <FilterChip
            className="sm:mt-6"
            facet={t('admin.filter.target')}
            value={search.target}
            onRemove={() => replaceSearch({ ...search, target: undefined })}
          />
        )}
      </>
    );

  return (
    <AdminConsole tab={tab} onTabChange={changeTab} filters={filters}>
      {tab === 'users' ? (
        <Panel
          query={users}
          more={moreUsers}
          emptyTitle={t('admin.empty.users')}
          render={(items) => <AdminUserTable items={items} />}
        />
      ) : tab === 'workspaces' ? (
        <Panel
          query={workspaces}
          more={moreWorkspaces}
          emptyTitle={t('admin.empty.workspaces')}
          render={(items) => <AdminWorkspaceTable items={items} />}
        />
      ) : tab === 'boards' ? (
        <Panel
          query={boards}
          more={moreBoards}
          emptyTitle={t('admin.empty.boards')}
          render={(items) => <AdminBoardTable items={items} />}
        />
      ) : (
        <Panel
          query={audit}
          more={moreAudit}
          emptyTitle={t('admin.empty.audit')}
          render={(items) => <AdminAuditTable items={items} />}
        />
      )}
    </AdminConsole>
  );
}

interface PagedQuery<Item> {
  readonly data:
    { readonly items: readonly Item[]; readonly nextCursor: string | null } | undefined;
  readonly isPending: boolean;
  readonly error: { readonly message: string } | null;
  readonly refetch: () => unknown;
}

function Panel<Item>({
  query,
  more,
  emptyTitle,
  render,
}: {
  readonly query: PagedQuery<Item>;
  readonly more: AdminLoadMore;
  readonly emptyTitle: string;
  readonly render: (items: readonly Item[]) => ReactElement;
}): ReactElement {
  const items = query.data?.items ?? [];
  return (
    <AdminPagedPanel
      loading={query.isPending}
      error={query.error?.message}
      onRetry={() => void query.refetch()}
      empty={query.data !== undefined && items.length === 0}
      emptyTitle={emptyTitle}
      hasMore={(query.data?.nextCursor ?? null) !== null}
      loadingMore={more.isLoading}
      loadMoreError={more.error?.message}
      onLoadMore={() => void more.loadMore()}
    >
      {render(items)}
    </AdminPagedPanel>
  );
}
